import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CrewShift, MotionDetectionState } from '../types';
import { getActiveCrewShift } from '../services/crewService';
import {
  startMotionDetection,
  stopMotionDetection,
  onMotionChange,
  getEprfEditRestrictions,
} from '../services/motionDetectionService';
import { useAuth } from '../hooks/useAuth';
import { useTenant } from './TenantContext';

interface CrewContextType {
  activeShift: CrewShift | null;
  motionState: MotionDetectionState | null;
  isMotionDetectionActive: boolean;
  crewSignedOn: boolean;
  eprfRestrictions: {
    canEdit: boolean;
    canRead: boolean;
    lockedFields: string[];
    reason?: string;
  };
  loadingShift: boolean;
}

const CrewContext = createContext<CrewContextType | undefined>(undefined);

interface CrewProviderProps {
  children: ReactNode;
  tenantId?: string;
  crewMemberId?: string;
}

export const CrewProvider: React.FC<CrewProviderProps> = ({
  children,
  tenantId,
  crewMemberId,
}) => {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const [activeShift, setActiveShift] = useState<CrewShift | null>(null);
  const [motionState, setMotionState] = useState<MotionDetectionState | null>(null);
  const [isMotionDetectionActive, setIsMotionDetectionActive] = useState(false);
  const [loadingShift, setLoadingShift] = useState(true);
  const [eprfRestrictions, setEprfRestrictions] = useState<CrewContextType['eprfRestrictions']>({
    canEdit: true,
    canRead: true,
    lockedFields: [],
  });

  // Load active shift on mount
  useEffect(() => {
    const resolvedTenantId = tenantId || user?.tenantId || tenant.tenantId;
    const resolvedCrewMemberId = crewMemberId || user?.uid;

    if (!resolvedTenantId || !resolvedCrewMemberId) {
      setLoadingShift(false);
      return;
    }

    const loadShift = async () => {
      try {
        const shift = await getActiveCrewShift(resolvedTenantId, resolvedCrewMemberId);
        setActiveShift(shift);

        // Start motion detection if shift is active
        if (shift) {
          startMotionDetection();
          setIsMotionDetectionActive(true);
        }
      } catch (error) {
        console.error('Failed to load crew shift:', error);
      } finally {
        setLoadingShift(false);
      }
    };

    loadShift();

    return () => {
      stopMotionDetection();
      setIsMotionDetectionActive(false);
    };
  }, [tenantId, crewMemberId, user?.tenantId, user?.uid, tenant.tenantId]);

  // Subscribe to motion changes
  useEffect(() => {
    if (!isMotionDetectionActive || !activeShift) return;

    const unsubscribe = onMotionChange((state) => {
      setMotionState(state);

      // Calculate ePRF restrictions based on motion
      const restrictions = getEprfEditRestrictions(
        activeShift.crewMemberRole,
        activeShift.id.split('-').length, // Simple crew count estimate
        state.isMoving
      );

      setEprfRestrictions(restrictions);
    });

    return unsubscribe;
  }, [isMotionDetectionActive, activeShift]);

  const value: CrewContextType = {
    activeShift,
    motionState,
    isMotionDetectionActive,
    crewSignedOn: activeShift !== null,
    eprfRestrictions,
    loadingShift,
  };

  return (
    <CrewContext.Provider value={value}>
      {children}
    </CrewContext.Provider>
  );
};

export const useCrewSession = (): CrewContextType => {
  const context = useContext(CrewContext);
  if (!context) {
    throw new Error('useCrewSession must be used within CrewProvider');
  }
  return context;
};

export const useOptionalCrewSession = (): CrewContextType => {
  const context = useContext(CrewContext);
  if (context) return context;

  return {
    activeShift: null,
    motionState: null,
    isMotionDetectionActive: false,
    crewSignedOn: true,
    eprfRestrictions: {
      canEdit: true,
      canRead: true,
      lockedFields: [],
    },
    loadingShift: false,
  };
};
